// The help centre's search: a small client-side index over every article (title, keywords, headings, description,
// body), built once from the bundled content. No server, no query ever leaves the browser.
//
// Scoring per query term (every term must match somewhere): title word 10 (prefix 6), keyword 7 (prefix 5),
// FAQ question 5, heading 4, description 3, body 1, plus a bonus when the whole query appears in the title, so title
// matches rank first. Filters: section, category (and the language, which picks the articles searched). Accents and case are
// ignored; for languages written without spaces (ja, zh, th) a term also matches as a substring.

import { isSeparateSection } from "./sections";
import type { SeoLang } from "@/lib/seo/site";
import type { HelpArticle } from "./index";

/** Lower case, accents removed, punctuation as spaces. */
export const normalise = (s: string): string =>
  s
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();

const words = (s: string): string[] => normalise(s).split(" ").filter(Boolean);

/** Too common to rank on (English only; other languages rely on the AND rule). */
const STOP = new Set(["a", "an", "and", "are", "can", "do", "for", "how", "i", "in", "is", "it", "my", "of", "on", "or", "the", "to", "what", "where", "why", "with", "you", "your"]);

export interface HelpIndexEntry {
  slug: string;
  lang: SeoLang;
  title: string;
  description: string;
  section: string;
  category: string;
  t: string[];
  k: string[];
  h: string[];
  d: string[];
  /** FAQ questions' words. */
  f: string[];
  b: Set<string>;
  /** Normalised full text (substring matching). */
  all: string;
  titleN: string;
}

export const buildHelpIndex = (articles: HelpArticle[]): HelpIndexEntry[] =>
  articles.map((a) => ({
    slug: a.slug,
    lang: a.lang,
    title: a.title,
    description: a.description,
    section: a.section,
    category: a.category,
    t: words(a.title),
    k: a.keywords.flatMap(words),
    h: a.headings.flatMap((x) => words(x.text)),
    d: words(a.description),
    f: a.faq.flatMap((x) => words(x.q)),
    b: new Set(words(a.text)),
    all: normalise([a.title, a.keywords.join(" "), a.description, a.text].join(" ")),
    titleN: normalise(a.title),
  }));

export interface HelpHit {
  slug: string;
  title: string;
  description: string;
  section: string;
  category: string;
  score: number;
}

const termScore = (e: HelpIndexEntry, term: string): number => {
  let s = 0;
  if (e.t.includes(term)) s += 10;
  else if (term.length >= 3 && e.t.some((w) => w.startsWith(term))) s += 6;
  if (e.k.includes(term)) s += 7;
  else if (term.length >= 3 && e.k.some((w) => w.startsWith(term))) s += 5;
  if (e.f.includes(term)) s += 5;
  else if (term.length >= 3 && e.f.some((w) => w.startsWith(term))) s += 3;
  if (e.h.includes(term)) s += 4;
  if (e.d.includes(term)) s += 3;
  if (e.b.has(term)) s += 1;
  else if (s === 0 && term.length >= 3 && [...e.b].some((w) => w.startsWith(term))) s += 0.5;
  // Scripts without word breaks, and anything else the words missed.
  if (s === 0 && e.all.includes(term)) s += 1;
  return s;
};

/**
 * The best matches for `query` in `lang` (articles without that language are searched in English), best first.
 * `section` limits to one section. Without it, the separate sections (sections.ts: the Photohawk app's help) are left
 * out: they're searched only from their own pages, which pass their section.
 */
export function searchHelp(index: HelpIndexEntry[], query: string, lang: SeoLang = "en", opts: { section?: string; category?: string; limit?: number } = {}): HelpHit[] {
  const q = normalise(query);
  if (!q) return [];
  const all = q.split(" ");
  const terms = all.filter((t) => !STOP.has(t));
  const use = terms.length ? terms : all;
  // One entry per slug: the requested language where it exists, else English.
  const langs = new Set(index.filter((e) => e.lang === lang).map((e) => e.slug));
  const pool = index.filter((e) => (e.lang === lang || (e.lang === "en" && !langs.has(e.slug))) && (opts.section ? e.section === opts.section : !isSeparateSection(e.section)) && (!opts.category || e.category === opts.category));
  const hits: HelpHit[] = [];
  for (const e of pool) {
    let score = 0;
    let ok = true;
    for (const t of use) {
      const s = termScore(e, t);
      if (s === 0) {
        ok = false;
        break;
      }
      score += s;
    }
    if (!ok) continue;
    if (use.length > 1 && e.titleN.includes(q)) score += 8;
    hits.push({ slug: e.slug, title: e.title, description: e.description, section: e.section, category: e.category, score });
  }
  return hits.sort((a, b) => b.score - a.score || a.title.localeCompare(b.title)).slice(0, opts.limit ?? 20);
}

/** The query's search terms (as searchHelp uses them), for highlighting. */
export const queryTerms = (query: string): string[] => {
  const all = normalise(query).split(" ").filter(Boolean);
  const terms = all.filter((t) => !STOP.has(t));
  return terms.length ? terms : all;
};

export interface Highlighted {
  text: string;
  hit: boolean;
}

const fold = (c: string) => c.toLowerCase().normalize("NFKD").replace(/[̀-ͯ]/g, "");

/**
 * `text` split into parts, with the words that match a search term marked (a word starting with a term, ignoring case
 * and accents; in scripts without spaces, any occurrence of the term).
 */
export const highlightParts = (text: string, terms: string[]): Highlighted[] => {
  // One-letter Latin terms would light up half the text; one CJK character is a word.
  const ts = terms.filter((t) => t.length >= 2 || !/^[\p{Script=Latin}\p{N}]$/u.test(t));
  if (!ts.length || !text) return [{ text, hit: false }];
  // A folded copy with a map back to the original positions (folding can drop combining marks).
  let folded = "";
  const at: number[] = [];
  for (let i = 0; i < text.length; i += 1) {
    const f = fold(text[i]);
    for (let k = 0; k < f.length; k += 1) {
      folded += f[k];
      at.push(i);
    }
  }
  const marks = new Array<boolean>(text.length).fill(false);
  for (const t of ts) {
    for (let i = folded.indexOf(t); i !== -1; i = folded.indexOf(t, i + 1)) {
      const spaced = /^[\p{Script=Latin}\p{Script=Cyrillic}\p{Script=Greek}\p{N}]/u.test(t);
      // Latin-like scripts: only at the start of a word, and the whole word lights up ("download" in "downloads").
      if (spaced && i > 0 && /[\p{L}\p{N}]/u.test(folded[i - 1])) continue;
      let end = i + t.length;
      if (spaced) while (end < folded.length && /[\p{L}\p{N}]/u.test(folded[end])) end += 1;
      for (let k = i; k < end && k < at.length; k += 1) marks[at[k]] = true;
    }
  }
  const out: Highlighted[] = [];
  for (let i = 0; i < text.length; i += 1) {
    const last = out[out.length - 1];
    if (last && last.hit === marks[i]) last.text += text[i];
    else out.push({ text: text[i], hit: marks[i] });
  }
  return out;
};
